import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { CheckCircle2, RotateCcw, Timer } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useCandidate,
  useCandidatesList,
  useEffectiveUser,
  useEnrollmentsForCandidate,
  useExamAttemptsForExpediente,
  useExpedienteForCandidateStandard,
  useRulesForStandard,
  useTheoreticalBank,
} from "@/store/hooks";

function useQuery() {
  const [loc] = useLocation();
  return useMemo(() => {
    const s = typeof window !== "undefined" ? window.location.search : "";
    return new URLSearchParams(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc, typeof window !== "undefined" ? window.location.search : ""]);
}

export default function TheoreticalExam() {
  const [, setLocation] = useLocation();
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("alumno"), [setRole]);

  const effective = useEffectiveUser();
  const candList = useCandidatesList();
  const candidateId =
    effective && effective.roles.includes("alumno") ? effective.id : candList[0]?.id;
  const me = useCandidate(candidateId);

  const enrollments = useEnrollmentsForCandidate(candidateId);
  const q = useQuery();
  const requested = q.get("codigo");
  const codigo = requested && enrollments.includes(requested) ? requested : enrollments[0];

  const bank = useTheoreticalBank(codigo);
  const rules = useRulesForStandard(codigo);
  const exp = useExpedienteForCandidateStandard(candidateId, codigo);
  const attempts = useExamAttemptsForExpediente(exp?.id).filter((a) => a.kind === "teorico");

  const setScore = useDemoStore((s) => s.setTheoreticalScore);
  const addAttempt = useDemoStore((s) => s.addExamAttempt);
  const updateExpediente = useDemoStore((s) => s.updateExpediente);
  const logEvent = useDemoStore((s) => s.logEvent);

  const threshold = useMemo(() => {
    const r = rules.find((r) => r.condicion.field === "examen_teorico.puntaje" && r.habilitada);
    return r && typeof r.condicion.value === "number" ? r.condicion.value : 80;
  }, [rules]);

  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitted, setSubmitted] = useState<{ score: number; aprobado: boolean } | null>(null);

  const computed = useMemo(() => {
    if (bank.length === 0) return 0;
    const correctas = bank.filter((q) => answers[q.id] === q.correcta).length;
    return Math.round((correctas / bank.length) * 100);
  }, [answers, bank]);

  if (!me || !codigo) {
    return (
      <AppLayout breadcrumb={["Mi ruta", "Examen teórico"]}>
        <Card><div style={{ padding: 24, color: "var(--cc-gray-600)" }}>
          {!me ? "No hay candidato activo." : "Sin estándar seleccionado o inscrito."}
        </div></Card>
      </AppLayout>
    );
  }

  const lastAttempt = attempts.length ? attempts[attempts.length - 1] : null;

  const onSubmit = () => {
    const score = computed;
    const aprobado = score >= threshold;
    setScore(score);
    if (exp) {
      addAttempt({
        id: `att-${Date.now().toString(36)}`,
        expedienteId: exp.id,
        candidatoId: me.id,
        estandar: codigo,
        kind: "teorico",
        fecha: new Date().toISOString().slice(0, 10),
        score,
        aprobado,
      });
      updateExpediente(exp.id, {
        examenTeorico: { aplicado: true, puntaje: score, aprobado, fecha: new Date().toISOString().slice(0, 10) },
      });
    }
    logEvent({ actor: me.nombre, rol: "alumno", accion: `Aplicó examen teórico (${score}/100, ${aprobado ? "aprobado" : "no aprobado"})`, entidad: "expediente", entidadId: exp?.id ?? "-" });
    setSubmitted({ score, aprobado });
  };

  const onRetry = () => {
    setAnswers({});
    setSubmitted(null);
  };

  return (
    <AppLayout breadcrumb={["Mi ruta", "Examen teórico"]}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>Examen teórico · {codigo}</h1>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>{bank.length} reactivos · Aprueba con ≥ {threshold}%</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", background: "var(--cc-gray-100)", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>
          <Timer size={14} /> 14:32 restantes
        </div>
      </div>

      {(submitted || lastAttempt) && (
        <Card style={{ background: (submitted ?? lastAttempt)!.aprobado ? "var(--cc-green-bg)" : "var(--cc-amber-bg)", border: `1px solid ${(submitted ?? lastAttempt)!.aprobado ? "#86efac" : "#fcd34d"}`, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CheckCircle2 size={22} color={(submitted ?? lastAttempt)!.aprobado ? "var(--cc-green)" : "var(--cc-amber)"} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }} data-testid="text-teo-score">
                  Examen {submitted ? "entregado" : "previo"} · Puntaje {(submitted ?? lastAttempt)!.score} / 100 — {(submitted ?? lastAttempt)!.aprobado ? "Aprobado" : "No aprobado"}
                </div>
                <div style={{ fontSize: 12, color: "var(--cc-gray-600)" }}>
                  {attempts.length + (submitted ? 1 : 0)} intento(s) registrado(s).
                </div>
              </div>
            </div>
            {submitted && (
              <PrimaryButton variant="ghost" onClick={onRetry} testId="button-retry-teo">
                <RotateCcw size={13} /> Repetir examen
              </PrimaryButton>
            )}
          </div>
        </Card>
      )}

      <Card>
        <SectionTitle>Reactivos del estándar</SectionTitle>
        {bank.length === 0 ? (
          <div style={{ padding: 14, color: "var(--cc-gray-600)", fontSize: 13 }}>
            Este estándar aún no tiene reactivos teóricos configurados. El administrador puede cargarlos desde el constructor de instrumentos.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {bank.map((q, i) => (
              <div key={q.id} data-testid={`question-${q.id}`} style={{ padding: 14, border: "1px solid var(--cc-gray-100)", borderRadius: 10 }}>
                <div style={{ display: "flex", gap: 8, fontSize: 12, color: "var(--cc-gray-500)", fontWeight: 600, marginBottom: 6 }}>REACTIVO {i + 1}</div>
                <div style={{ fontSize: 13, color: "var(--cc-navy)", fontWeight: 600, marginBottom: 10 }}>{q.enunciado}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {q.opciones.map((opt, idx) => {
                    const selected = answers[q.id] === idx;
                    return (
                      <label
                        key={idx}
                        data-testid={`option-${q.id}-${idx}`}
                        style={{
                          display: "flex", alignItems: "center", gap: 10, padding: 10, borderRadius: 8,
                          border: `1px solid ${selected ? "var(--cc-teal)" : "var(--cc-gray-200)"}`,
                          background: selected ? "var(--cc-teal-bg)" : "white",
                          cursor: "pointer", fontSize: 13, color: "var(--cc-gray-700)",
                        }}
                      >
                        <input
                          type="radio"
                          name={q.id}
                          checked={selected}
                          onChange={() => setAnswers((a) => ({ ...a, [q.id]: idx }))}
                          style={{ accentColor: "var(--cc-teal)" }}
                        />
                        {opt}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
          <PrimaryButton variant="ghost" onClick={() => setLocation("/alumno")} testId="button-back">← Salir sin guardar</PrimaryButton>
          <div style={{ display: "flex", gap: 8 }}>
            <Link href={`/alumno/expediente?codigo=${codigo}`}><PrimaryButton variant="ghost">Ir a mi expediente</PrimaryButton></Link>
            <PrimaryButton onClick={onSubmit} testId="button-submit-exam" disabled={bank.length === 0}>
              Enviar examen
            </PrimaryButton>
          </div>
        </div>
      </Card>
    </AppLayout>
  );
}
